import { FC } from "react";

import { DateTime } from "@bps/date-time";
import {
  Heading,
  IDialogContentProps,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { DatePickerField } from "@ui-components/form/DatePickerField.tsx";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";

import { ActivityDescriptionCell } from "./ActivityDescriptionCell.tsx";
import { FollowupReminderCurrentStatus } from "./FollowupReminderStatus.tsx";
import { ClinicalActivityTableRow } from "./types/clinical-activity-table.type.ts";

export type ChangeDueDateValues = {
  dueDate: string;
  comment: string;
};

type ChangeDueDateFormValues = {
  dueDate?: Date;
  comment?: string;
};

type ChangeDueDateDialogProps = {
  item: ClinicalActivityTableRow;
  existingComment?: string;
  onDismiss: () => void;
  onSave: (values: ChangeDueDateValues) => Promise<void>;
};

const nameOf = nameOfFactory<ChangeDueDateFormValues>();

const validate = (values: ChangeDueDateFormValues) =>
  values.dueDate ? {} : { dueDate: "Date is required" };

export const ChangeDueDateDialog: FC<ChangeDueDateDialogProps> = ({
  item,
  existingComment: existingCommentProp,
  onDismiss,
  onSave
}) => {
  const theme = useTheme();
  const existingComment = existingCommentProp?.trim() ?? "";
  const hasExistingComment = existingComment.length > 0;

  const patientName = item.patient
    ? `${item.patient.lastName}, ${item.patient.firstName}`
    : "";

  const currentDueDate =
    item.clinicalReminderComm?.rescheduledDueDate ?? item.activity.dueDate;

  const dueDateText = currentDueDate
    ? DateTime.fromISO(currentDueDate).toDayDefaultFormat()
    : "-";

  const labelStyle = {
    root: { fontSize: 11, color: theme.palette.neutralSecondary }
  };

  const dialogContentProps: IDialogContentProps = {
    title: (
      <Heading variant="modal-heading">
        {`Change reminder due date${patientName ? ` for ${patientName}` : ""}`}
      </Heading>
    ),
    showCloseButton: true
  };

  const onSubmit = async (values: ChangeDueDateFormValues) => {
    if (!values.dueDate) return;

    const newComment = values.comment?.trim() ?? "";
    const combinedComment = hasExistingComment
      ? [existingComment, newComment].filter(Boolean).join("\n")
      : newComment;

    await onSave({
      dueDate: DateTime.jsDateToISODate(values.dueDate),
      comment: combinedComment
    });
  };

  return (
    <SubmissionFormDialog<ChangeDueDateFormValues>
      dialogName="Change reminder due date"
      onSubmit={onSubmit}
      initialValues={{}}
      onSubmitSucceeded={onDismiss}
      validate={validate}
      buttonsProps={{
        submitButtonProps: {
          text: "Save & move to Clinical reminders",
          iconProps: {}
        },
        hideButtonsSeparator: true
      }}
      dialogProps={{
        dialogContentProps,
        onDismiss,
        minWidth: 480,
        maxWidth: 480
      }}
    >
      {() => (
        <Fieldset>
          <Stack tokens={{ childrenGap: 4 }}>
            <Text styles={{ root: { fontWeight: 600 } }}>Reminder</Text>
            <Stack
              horizontal
              tokens={{ childrenGap: 16, padding: 8 }}
              styles={{
                root: { border: `1px solid ${theme.palette.neutralLight}` }
              }}
            >
              <Stack grow tokens={{ childrenGap: 4 }}>
                <Text styles={labelStyle}>Reason</Text>
                <ActivityDescriptionCell activity={item.activity} />
                <Text styles={labelStyle}>Due</Text>
                <Text>{dueDateText}</Text>
              </Stack>
              <Stack grow tokens={{ childrenGap: 4 }}>
                <Text styles={labelStyle}>Current status</Text>
                <FollowupReminderCurrentStatus reminder={item} />
              </Stack>
            </Stack>
          </Stack>

          <DatePickerField
            name={nameOf("dueDate")}
            label="Date"
            required
            minDate={DateTime.jsDateNow()}
          />

          {hasExistingComment && (
            <Stack tokens={{ childrenGap: 4 }}>
              <Text styles={{ root: { fontWeight: 600 } }}>Comment</Text>
              <Text styles={{ root: { whiteSpace: "pre-wrap" } }}>
                {existingComment}
              </Text>
            </Stack>
          )}

          <TextInputField
            name={nameOf("comment")}
            label={hasExistingComment ? "Additional comment" : "Comment"}
          />
        </Fieldset>
      )}
    </SubmissionFormDialog>
  );
};
